import { useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { ResponseErrorPanel } from '@backstage/core-components';
import { azureDevOpsApiRef } from '@backstage/plugin-azure-devops';
import { Link } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { Table, TableColumn } from '@backstage/core-components';
import { useAsync } from 'react-use';
import { DashboardPullRequest } from '@backstage/plugin-azure-devops-common';

type PullRequestRow = {
  id: number;
  title: string;
  author: string;
  repository: string;
  createdAt: string;
  createdAtSort: number;
  link?: string;
};

const columns: TableColumn<PullRequestRow>[] = [
  {
    title: 'PR',
    field: 'title',
    render: row =>
      row.link ? (
        <Link href={row.link} target="_blank" rel="noopener noreferrer">
          {row.title}
        </Link>
      ) : (
        row.title
      ),
  },
  { title: 'Author', field: 'author' },
  { title: 'Repository', field: 'repository' },
  { title: 'Created', field: 'createdAt', defaultSort: 'desc' },
];

const isToday = (isoDate?: string) => {
  if (!isoDate) {
    return false;
  }

  const created = new Date(isoDate);
  if (Number.isNaN(created.getTime())) {
    return false;
  }

  const now = new Date();
  return (
    created.getFullYear() === now.getFullYear() &&
    created.getMonth() === now.getMonth() &&
    created.getDate() === now.getDate()
  );
};

const toRow = (pr: DashboardPullRequest): PullRequestRow | undefined => {
  const created = pr.creationDate;
  const pullRequestId = pr.pullRequestId;
  if (!pullRequestId || !isToday(created)) {
    return undefined;
  }

  const createdDate = created ? new Date(created) : undefined;
  const createdAt = createdDate ? createdDate.toLocaleString() : 'Unknown';
  const createdAtSort = createdDate ? createdDate.getTime() : 0;

  return {
    id: pullRequestId,
    title: pr.title ?? `PR #${pullRequestId}`,
    author: pr.createdBy?.displayName ?? pr.createdBy?.uniqueName ?? 'Unknown',
    repository: pr.repository?.name ?? 'Unknown',
    createdAt,
    createdAtSort,
    link: pr.link,
  };
};

export const AzureTodayPullRequestsPage = () => {
  const azureDevOpsApi = useApi(azureDevOpsApiRef);

  const { value, loading, error } = useAsync(
    () => azureDevOpsApi.getDashboardPullRequests('Zest Development', 200),
    [azureDevOpsApi],
  );

  const rows = useMemo(() => {
    return (value ?? [])
      .map(toRow)
      .filter((row): row is PullRequestRow => Boolean(row))
      .sort((a, b) => b.createdAtSort - a.createdAtSort);
  }, [value]);

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <InfoCard title="Today's Pull Requests">
      <Table
        title="Azure DevOps"
        isLoading={loading}
        options={{ search: true, paging: true, pageSize: 20 }}
        columns={columns}
        data={rows}
      />
    </InfoCard>
  );
};